import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Chip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { chipsDoDia, URL_DATE_FORMAT } from './constants';

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const buildPeriodCells = (inicio, fim) => {
  const start = moment(inicio).startOf('day');
  const end = moment(fim).startOf('day');
  if (!start.isValid() || !end.isValid() || start.isAfter(end, 'day')) return [];
  const cells = [];
  for (let i = 0; i < start.day(); i += 1) cells.push(null);
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'day')) {
    cells.push(cursor.clone());
    cursor.add(1, 'day');
  }
  return cells;
};

const chipColor = (severidade) => {
  if (severidade === 'ALTA') return 'error';
  if (severidade === 'MEDIA') return 'warning';
  return 'default';
};

const accentColor = (severidade) => {
  if (severidade === 'ALTA') return 'error.main';
  if (severidade === 'MEDIA') return 'warning.main';
  if (severidade === 'BAIXA') return 'text.secondary';
  return 'divider';
};

const cellBg = (theme, severidade, selected) => {
  if (selected) return alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1);
  const dark = theme.palette.mode === 'dark';
  if (severidade === 'ALTA') return alpha(theme.palette.error.main, dark ? 0.1 : 0.06);
  if (severidade === 'MEDIA') return alpha(theme.palette.warning.main, dark ? 0.1 : 0.08);
  if (severidade === 'BAIXA') return alpha(theme.palette.text.primary, 0.04);
  return 'background.paper';
};

const CalendarioCiclo = ({
  inicio,
  fim,
  dias,
  selectedKey,
  onSelect,
  pessoal,
  atenuarDemais,
}) => {
  const cells = useMemo(() => buildPeriodCells(inicio, fim), [inicio, fim]);
  const byDate = useMemo(() => {
    const map = {};
    (dias || []).forEach((dia) => {
      if (dia?.dia) map[dia.dia] = dia;
    });
    return map;
  }, [dias]);
  const showYear = moment(inicio).year() !== moment(fim).year();
  const atenuar = Boolean(atenuarDemais && selectedKey);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 0.75,
      }}
    >
      {WEEKDAYS.map((label, index) => (
        <Typography
          key={`w-${index}`}
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: 'center', fontWeight: 700 }}
        >
          {label}
        </Typography>
      ))}
      {cells.map((day, index) => {
        if (!day) return <Box key={`empty-${index}`} />;
        const key = day.format(URL_DATE_FORMAT);
        const bucket = byDate[key];
        const chips = chipsDoDia(bucket?.problemas, { pessoal });
        const selected = selectedKey === key;
        const severidade = bucket?.severidade;
        return (
          <Box
            key={key}
            onClick={() => onSelect(selected ? null : key)}
            sx={(theme) => ({
              minHeight: pessoal ? 88 : 76,
              p: 0.75,
              borderRadius: 1,
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              borderLeftWidth: selected ? 1 : 3,
              borderLeftColor: selected ? 'primary.main' : accentColor(severidade),
              cursor: 'pointer',
              overflow: 'hidden',
              bgcolor: cellBg(theme, severidade, selected),
              opacity: atenuar && !selected ? 0.4 : 1,
              transition: 'opacity 120ms ease',
              '&:hover': { opacity: 1 },
            })}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: showYear ? 10 : 12, lineHeight: 1.2 }}>
              {day.format(showYear ? 'DD/MM/YYYY' : 'DD/MM')}
            </Typography>
            {chips.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, mt: 0.5 }}>
                {chips.map((chip) => (
                  <Chip
                    key={chip.key}
                    size="small"
                    label={chip.label}
                    color={chipColor(chip.severidade)}
                    variant="outlined"
                    title={chip.label}
                    sx={{
                      height: 20,
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        px: 0.75,
                        fontSize: 11,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

CalendarioCiclo.propTypes = {
  inicio: PropTypes.number,
  fim: PropTypes.number,
  dias: PropTypes.array,
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  pessoal: PropTypes.bool,
  atenuarDemais: PropTypes.bool,
};

export default CalendarioCiclo;
